import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Client from './client';
import isEmpty from 'lodash/fp/isEmpty';
import { ERROR_CODES } from '#/guest-aps/config';
import { useParkinglotMetadataSaveStorage } from '#/guest-aps/hooks/use-parkinglot-metadata-save-storage';
import type { UseQueryOptions } from '@tanstack/react-query';

export const useParkinglotByCode = (
  {
    parkinglotCode,
    serialCode,
  }: { parkinglotCode: string; serialCode: string },
  options: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof Client.fetchParkinglotBySerialCode>>
    >,
    'queryFn' | 'queryKey'
  > = {},
) => {
  const navigate = useNavigate();
  const { data, status, error } = useQuery({
    queryKey: ['partkinglot', 'from_serial_code', serialCode],
    queryFn: () =>
      Client.fetchParkinglotBySerialCode({ parkinglotCode, serialCode }),
    refetchInterval: false,
    enabled: !!serialCode,
    ...options,
  });

  const { parkinglot, enabled, ...rest } = data?.data?.data ?? {};
  const metadata = { enabled, ...rest };

  useParkinglotMetadataSaveStorage(parkinglot);

  useEffect(() => {
    if (status === 'error') navigate(`/error/?code=${ERROR_CODES.NO_EXIST}`);
    if (enabled === false) navigate(`/error?code=${ERROR_CODES.APS_DISABLED}`);
  }, [enabled, status, navigate]);

  return {
    parkinglot: data?.data?.data?.parkinglot ?? null,
    metadata: isEmpty(data?.data?.data) ? null : metadata,
    getParkinglotFromCodeStatus: status,
    getParkinglotFromCodeError: error,
  };
};
